import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import * as settingsApi from '@/api/settings'

const DEFAULT_TITLE = 'Bekat KB'
const DEFAULT_CUSTOMER_LABEL = 'Customer'
const DEFAULT_LOGO_TEXT = 'BK'
const DEFAULT_VERSION = '0.9.0'

const SiteSettingsContext = createContext({
  siteTitle: DEFAULT_TITLE,
  customerLabel: DEFAULT_CUSTOMER_LABEL,
  logoText: DEFAULT_LOGO_TEXT,
  appVersion: DEFAULT_VERSION,
  loading: true,
  refresh: async () => {},
  applySettings: () => {},
})

export function SiteSettingsProvider({ children }) {
  const [siteTitle, setSiteTitle] = useState(DEFAULT_TITLE)
  const [customerLabel, setCustomerLabel] = useState(DEFAULT_CUSTOMER_LABEL)
  const [logoText, setLogoText] = useState(DEFAULT_LOGO_TEXT)
  const [appVersion, setAppVersion] = useState(DEFAULT_VERSION)
  const [loading, setLoading] = useState(true)

  const applySettings = useCallback((data) => {
    if (data?.site_title) setSiteTitle(data.site_title)
    if (data?.customer_label) setCustomerLabel(data.customer_label)
    if (data?.logo_text) setLogoText(data.logo_text)
    if (data?.app_version) setAppVersion(data.app_version)
  }, [])

  const refresh = useCallback(async () => {
    try {
      const data = await settingsApi.getSiteSettings()
      applySettings(data)
    } catch {
      // keep defaults
    } finally {
      setLoading(false)
    }
  }, [applySettings])

  useEffect(() => {
    refresh()
  }, [refresh])

  useEffect(() => {
    document.title = siteTitle
  }, [siteTitle])

  const value = useMemo(
    () => ({
      siteTitle,
      customerLabel,
      logoText,
      appVersion,
      loading,
      refresh,
      applySettings,
    }),
    [siteTitle, customerLabel, logoText, appVersion, loading, refresh, applySettings],
  )

  return (
    <SiteSettingsContext.Provider value={value}>{children}</SiteSettingsContext.Provider>
  )
}

export function useSiteSettings() {
  return useContext(SiteSettingsContext)
}
